import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { PageErrorBoundary } from '@appic/ui-components'
import { AppLayout } from './layout/AppLayout'
import { useLanguage } from '@/contexts/LanguageContext'
import { lazyRoute } from './lazy-route'
import { renderScoutRoutes } from './scout-routes'
import { renderWarehouseRoutes } from './warehouse-routes'
import { renderLearningRoutes } from './learning-routes'
import { renderSettingsRoutes } from './settings-routes'
import { renderMiscRoutes } from './misc-routes'

const AuthCallback = React.lazy(() => import('@/pages/auth/AuthCallback'))
const SignIn = React.lazy(() => import('@/pages/auth/SignIn'))
// ScanReview — MO-BESTELLSCHEIN-WA S-WA-03. Public route (no login;
// signed token in query string IS the auth — issued by SL S-WA-01).
const ScanReview = React.lazy(() => import('@/pages/scout/ScanReview'))
const OnboardingLanding = React.lazy(() => import('@/pages/onboarding/OnboardingLanding'))
const OnboardingVerify = React.lazy(() => import('@/pages/onboarding/OnboardingVerify'))
const OnboardingFlows = React.lazy(() => import('@/pages/onboarding/OnboardingFlows'))
const OnboardingMicrosoftPage = React.lazy(() => import('@/pages/onboarding/OnboardingMicrosoftPage'))
const OnboardingOdooPage = React.lazy(() => import('@/pages/onboarding/OnboardingOdooPage'))
const OnboardingDone = React.lazy(() => import('@/pages/onboarding/OnboardingDone'))

// Route-coverage manifest: guard-route-coverage scans this file for page
// imports, so every routed page must appear here even when the actual
// <Route> declaration lives in a per-domain route module. Keep in sync
// with scout-routes, warehouse-routes, learning-routes, settings-routes,
// misc-routes.
// import('@/pages/navigation/AppOverview')
// import('@/pages/style-guide/StyleGuide')
// import('@/pages/review/PlatformComponentReview')
// import('@/pages/scout/ComponentReview')
// import('@/pages/atc/AgentConsoleDevPage')
// import('@/pages/atc/BridgeIaShell')
// import('@/pages/atc/BridgePage')
// import('@/pages/atc/bridge/OperationsManifestRoute')
// import('@/pages/atc/bridge/OperationsEscalationsRoute')
// import('@/pages/atc/bridge/ReposPrsRoute')
// import('@/pages/atc/bridge/ReposStagingRoute')
// import('@/pages/atc/bridge/AgentsRegistryRoute')
// import('@/pages/atc/bridge/AgentsChatRoute')
// import('@/pages/atc/CallingDashboard')
// import('@/pages/atc/TimesheetReview')
// import('@/pages/atc/DocsPage')
// import('@/pages/atc/DocDetailPage')
// import('@/pages/atc/DecisionsPage')
// import('@/pages/warehouse/WacklerBills')
// import('@/pages/warehouse/WacklerInvoiceDetail')
// import('@/pages/warehouse/PaymentOverview')
// import('@/pages/warehouse/OperationsLedger')
// import('@/pages/warehouse/CommunicationsTimeline')
// import('@/pages/warehouse/KdpRegistry')
// import('@/pages/warehouse/RatesConfig')
// import('@/pages/wackler/InvoiceResponse')
// import('@/pages/scout/Wochenplanung')
// import('@/pages/scout/Tagesplanung')
// import('@/pages/scout/Praeferenzen')
// import('@/pages/scout/MissionDetail')
// import('@/pages/scout/Unternehmenssuche')
// import('@/pages/scout/MissionControl')
// import('@/pages/scout/Scorecard')
// import('@/pages/scout/Hilfe')
// import('@/pages/scout/VisitReport')
// import('@/pages/scout/LeadSkipFlow')
// import('@/pages/scout/CampaignCockpit')
// import('@/pages/scout/PreQualCalling')
// import('@/pages/scout/CallingCockpit')
// import('@/pages/scout/CallTest')
// import('@/pages/scout/DialerConfig')
// import('@/pages/scout/Timesheet')
// import('@/pages/scout/SessionHistory')
// import('@/pages/scout/CustomerView')
// import('@/pages/scout/InsightsDashboard')
// import('@/pages/scout/ScanOrder')
// import('@/pages/scout/ScanReview')
// import('@/pages/scout/ProductsInfo')
// import('@/pages/scout/IndustriesInfo')
// import('@/pages/scout/Quiz')
// import('@/pages/scout/Customer360')
// import('@/pages/scout/CompanyDrawerPreview')
// import('@/pages/learning/CurriculumPlanning')
// import('@/pages/learning/LearnerDashboard')
// import('@/pages/learning/AccessibleLearnerDashboard')
// import('@/pages/learning/LehrplanPage')
// import('@/pages/settings/Settings')
// import('@/pages/settings/Company')
// import('@/pages/settings/UserManagement')
// import('@/pages/settings/ThemeSettings')

export const AppRoutes: React.FC = () => {
  const { t } = useLanguage()
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/start" replace />} />

      {/* Public routes (must be before path="/" layout so they match) */}
      <Route path="signin" element={lazyRoute(SignIn, 'Sign In')} />
      <Route path="auth/callback" element={lazyRoute(AuthCallback, 'Processing authentication...')} />
      {/* MO-BESTELLSCHEIN-WA S-WA-03 — token-authed Bestellschein review.
          Path stays top-level (not under any authed shell) because reps
          open this from a WhatsApp link on a phone that has never signed
          in to portal. The signed `?token=` is the auth, validated by
          SL on every request — no cookies, no Authorization header. */}
      <Route path="scan/review/:scanId" element={lazyRoute(ScanReview, 'Bestellung wird geladen...')} />
      <Route path="onboarding" element={lazyRoute(OnboardingLanding, 'Onboarding')} />
      <Route path="onboarding/verify" element={lazyRoute(OnboardingVerify, 'Onboarding — Code')} />
      <Route path="onboarding/flows" element={lazyRoute(OnboardingFlows, 'Onboarding — Auswahl')} />
      <Route path="onboarding/microsoft" element={lazyRoute(OnboardingMicrosoftPage, 'Onboarding — Microsoft')} />
      <Route path="onboarding/odoo" element={lazyRoute(OnboardingOdooPage, 'Onboarding — Odoo')} />
      <Route path="onboarding/done" element={lazyRoute(OnboardingDone, 'Onboarding — Fertig')} />

      {/* ERP Routes with AppLayout */}
      <Route path="/" element={<AppLayout />}>
        {renderMiscRoutes()}
        {renderWarehouseRoutes()}
        {renderScoutRoutes(t('app.routes.loadingSettings'))}
        {renderLearningRoutes()}
        {renderSettingsRoutes()}
      </Route>

      {/* Fallback for unknown routes */}
      <Route path="*" element={
        <PageErrorBoundary>
          <Navigate to="/start" replace />
        </PageErrorBoundary>
      } />
    </Routes>
  )
}
